/**
 * EmailChangeCard — Settings block that lets a user change their email address
 * with a 6-digit verification code sent to the NEW address (valid 15 min).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// v49.4 — 13-language dictionary for the "Change email" card
const EC_L = {
  fr: { title: "Changer d'adresse email", current: 'Actuelle', placeholder: 'nouvelle@adresse.com', send: 'Envoyer le code', codeSentTo: 'Un code à 6 chiffres a été envoyé à', validFor: 'Valide 15 minutes.', validate: 'Valider', cancel: 'Annuler', invalid: 'Adresse email invalide', codeSent: 'Code envoyé à', codeNeed: 'Code à 6 chiffres requis', updated: 'Adresse email mise à jour ✓', codeInvalid: 'Code invalide' },
  en: { title: 'Change email address', current: 'Current', placeholder: 'new@address.com', send: 'Send code', codeSentTo: 'A 6-digit code was sent to', validFor: 'Valid 15 minutes.', validate: 'Confirm', cancel: 'Cancel', invalid: 'Invalid email', codeSent: 'Code sent to', codeNeed: '6-digit code required', updated: 'Email address updated ✓', codeInvalid: 'Invalid code' },
  es: { title: 'Cambiar dirección de email', current: 'Actual', placeholder: 'nueva@direccion.com', send: 'Enviar código', codeSentTo: 'Se envió un código de 6 dígitos a', validFor: 'Válido 15 minutos.', validate: 'Confirmar', cancel: 'Cancelar', invalid: 'Email inválido', codeSent: 'Código enviado a', codeNeed: 'Se requiere código de 6 dígitos', updated: 'Email actualizado ✓', codeInvalid: 'Código inválido' },
  pt: { title: 'Alterar endereço de email', current: 'Atual', placeholder: 'novo@endereco.com', send: 'Enviar código', codeSentTo: 'Um código de 6 dígitos foi enviado para', validFor: 'Válido 15 minutos.', validate: 'Confirmar', cancel: 'Cancelar', invalid: 'Email inválido', codeSent: 'Código enviado a', codeNeed: 'Código de 6 dígitos necessário', updated: 'Email atualizado ✓', codeInvalid: 'Código inválido' },
  de: { title: 'E-Mail-Adresse ändern', current: 'Aktuell', placeholder: 'neue@adresse.de', send: 'Code senden', codeSentTo: 'Ein 6-stelliger Code wurde gesendet an', validFor: 'Gültig 15 Minuten.', validate: 'Bestätigen', cancel: 'Abbrechen', invalid: 'Ungültige E-Mail', codeSent: 'Code gesendet an', codeNeed: '6-stelliger Code erforderlich', updated: 'E-Mail aktualisiert ✓', codeInvalid: 'Ungültiger Code' },
  it: { title: "Cambia indirizzo email", current: 'Attuale', placeholder: 'nuovo@indirizzo.it', send: 'Invia codice', codeSentTo: 'Un codice a 6 cifre è stato inviato a', validFor: 'Valido 15 minuti.', validate: 'Conferma', cancel: 'Annulla', invalid: 'Email non valida', codeSent: 'Codice inviato a', codeNeed: 'Codice a 6 cifre richiesto', updated: 'Email aggiornata ✓', codeInvalid: 'Codice non valido' },
  ru: { title: 'Изменить email', current: 'Текущий', placeholder: 'novy@adres.ru', send: 'Отправить код', codeSentTo: '6-значный код отправлен на', validFor: 'Действителен 15 минут.', validate: 'Подтвердить', cancel: 'Отмена', invalid: 'Неверный email', codeSent: 'Код отправлен на', codeNeed: 'Требуется 6-значный код', updated: 'Email обновлён ✓', codeInvalid: 'Неверный код' },
  ar: { title: 'تغيير عنوان البريد الإلكتروني', current: 'الحالي', placeholder: 'new@address.com', send: 'إرسال الرمز', codeSentTo: 'تم إرسال رمز مكوّن من 6 أرقام إلى', validFor: 'صالح لمدة 15 دقيقة.', validate: 'تأكيد', cancel: 'إلغاء', invalid: 'بريد غير صالح', codeSent: 'أُرسل الرمز إلى', codeNeed: 'مطلوب رمز من 6 أرقام', updated: 'تم تحديث البريد ✓', codeInvalid: 'رمز غير صالح' },
  zh: { title: '更改邮箱地址', current: '当前', placeholder: 'new@address.com', send: '发送验证码', codeSentTo: '6位验证码已发送至', validFor: '有效期15分钟。', validate: '确认', cancel: '取消', invalid: '邮箱无效', codeSent: '验证码已发送至', codeNeed: '需要6位验证码', updated: '邮箱已更新 ✓', codeInvalid: '验证码错误' },
  ja: { title: 'メールアドレスを変更', current: '現在', placeholder: 'new@address.com', send: 'コードを送信', codeSentTo: '6桁のコードを送信しました', validFor: '15分間有効。', validate: '確定', cancel: 'キャンセル', invalid: '無効なメールアドレス', codeSent: 'コード送信先:', codeNeed: '6桁コードが必要です', updated: 'メールアドレスを更新しました ✓', codeInvalid: '無効なコード' },
  hi: { title: 'ईमेल पता बदलें', current: 'वर्तमान', placeholder: 'new@address.com', send: 'कोड भेजें', codeSentTo: '6-अंकों का कोड भेजा गया', validFor: '15 मिनट के लिए मान्य।', validate: 'पुष्टि करें', cancel: 'रद्द करें', invalid: 'अमान्य ईमेल', codeSent: 'कोड भेजा गया', codeNeed: '6-अंकों का कोड आवश्यक', updated: 'ईमेल अपडेट हो गया ✓', codeInvalid: 'अमान्य कोड' },
  nl: { title: 'E-mailadres wijzigen', current: 'Huidig', placeholder: 'nieuw@adres.nl', send: 'Code verzenden', codeSentTo: 'Een 6-cijferige code is verzonden naar', validFor: '15 minuten geldig.', validate: 'Bevestigen', cancel: 'Annuleren', invalid: 'Ongeldig e-mailadres', codeSent: 'Code verzonden naar', codeNeed: '6-cijferige code vereist', updated: 'E-mailadres bijgewerkt ✓', codeInvalid: 'Ongeldige code' },
  sw: { title: 'Badilisha anwani ya barua pepe', current: 'Ya sasa', placeholder: 'mpya@anwani.com', send: 'Tuma msimbo', codeSentTo: 'Msimbo wa tarakimu 6 umetumwa kwa', validFor: 'Halali dakika 15.', validate: 'Thibitisha', cancel: 'Ghairi', invalid: 'Barua pepe si sahihi', codeSent: 'Msimbo umetumwa kwa', codeNeed: 'Msimbo wa tarakimu 6 unahitajika', updated: 'Barua pepe imesasishwa ✓', codeInvalid: 'Msimbo si sahihi' },
  ht: { title: "Chanje imel", current: 'Aktyèl', placeholder: 'nouvo@adrès.com', send: 'Voye kòd la', codeSentTo: 'Yon kòd 6 chif voye bay', validFor: 'Valab 15 minit.', validate: 'Valide', cancel: 'Anile', invalid: 'Imel envalid', codeSent: 'Kòd voye bay', codeNeed: 'Kòd 6 chif obligatwa', updated: 'Imel mete a jou ✓', codeInvalid: 'Kòd envalid' },
};

const EmailChangeCard = ({ currentEmail, onChanged }) => {
  const { language } = useLanguage();
  const l = EC_L[language] || EC_L.fr;
  const [step, setStep] = useState('idle'); // idle | codeSent
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    const clean = (newEmail || '').trim().toLowerCase();
    if (!clean || !clean.includes('@')) {
      toast.error(l.invalid);
      return;
    }
    setBusy(true);
    try {
      await axios.post('/api/auth/me/email/change/start', { new_email: clean });
      toast.success(`${l.codeSent} ${clean}`);
      setStep('codeSent');
      setCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || l.invalid);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    const c = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(c)) {
      toast.error(l.codeNeed);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post('/api/auth/me/email/change/verify', { code: c });
      toast.success(l.updated);
      setStep('idle');
      setNewEmail('');
      setCode('');
      onChanged?.(r.data?.email);
    } catch (e) {
      toast.error(e.response?.data?.detail || l.codeInvalid);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="email-change-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
          <Mail className="w-5 h-5 text-purple-300" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">{l.title}</div>
          <div className="text-xs text-white/50">{l.current} : {currentEmail || '—'}</div>
        </div>
      </div>

      {step === 'idle' ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={l.placeholder}
            className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-400/50"
            data-testid="email-change-new-input"
          />
          <button
            type="button"
            onClick={start}
            disabled={busy || !newEmail}
            className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
            data-testid="email-change-request-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {l.send}
          </button>
        </div>
      ) : (
        <>
          <div className="text-xs text-white/60 mb-3">
            {l.codeSentTo} <span className="text-white font-semibold">{newEmail}</span>. {l.validFor}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="123456"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-white text-center tracking-[0.5em] font-mono text-lg focus:outline-none focus:border-purple-400/50"
              data-testid="email-change-code-input"
            />
            <button
              type="button"
              onClick={verify}
              disabled={busy || code.length !== 6}
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              data-testid="email-change-verify-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : l.validate}
            </button>
          </div>
          <button
            type="button"
            onClick={() => { setStep('idle'); setCode(''); }}
            className="text-xs text-white/40 hover:text-white/60"
          >
            {l.cancel}
          </button>
        </>
      )}
    </div>
  );
};

export default EmailChangeCard;
